import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { roleAPI, permissionAPI } from '../services/api';

const RolesPermissions = () => {
  const [activeTab, setActiveTab] = useState('roles');
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [permissionsList, setPermissionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showPermissionsEditor, setShowPermissionsEditor] = useState(false);
  const [showRoleCreationFlow, setShowRoleCreationFlow] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [newRoleData, setNewRoleData] = useState(null);
  const [categories, setCategories] = useState([]);

  const getCategoryDescription = (category) => {
    const descriptions = {
      'Dashboard': 'Controls access to the main dashboard page and analytics widgets',
      'User Management': 'Permissions for the Users page - creating, editing, and managing user accounts',
      'Recruiter Management': 'Controls the Recruiters page - registration, editing, and bulk upload features',
      'Student Management': 'Permissions for managing student admissions and enrollments - recording when students are admitted or enroll',
      'Payment Management': 'Controls the Payments section - processing, uploads, approvals, and payment reports',
      'Reports': 'Permissions for the Reports section - viewing, generating, and exporting various reports',
      'System Administration': 'Advanced features - system settings, audit logs, and this roles management page',
      'Profile': 'Basic profile page access - viewing and editing own profile information'
    };
    return descriptions[category] || `Controls access to ${category.toLowerCase()} features and actions`;
  };

  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: ''
  });

  const [permissionForm, setPermissionForm] = useState({
    name: '',
    display_name: '',
    description: '',
    category: '',
    resource: '',
    action: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesResponse, permissionsResponse, categoriesResponse] = await Promise.all([
        roleAPI.getAll(),
        permissionAPI.getAll(),
        permissionAPI.getCategories()
      ]);

      setRoles(rolesResponse.data);
      setPermissions(permissionsResponse.data);
      setCategories(categoriesResponse.data);

      // Flatten permissions for easier access
      const flatPermissions = [];
      Object.values(permissionsResponse.data).forEach(categoryPerms => {
        flatPermissions.push(...categoryPerms);
      });
      setPermissionsList(flatPermissions);
    } catch (error) {
      toast.error('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();

    // Instead of creating the role immediately, store the data and show permissions editor
    setNewRoleData(roleForm);
    setShowRoleModal(false);
    setShowRoleCreationFlow(true);

    // Prepare permissions list for the new role (all permissions set to false initially)
    const allPermissions = permissionsList.map(permission => ({
      ...permission,
      granted: false
    }));

    setSelectedRole({
      ...roleForm,
      permissions: allPermissions,
      isNew: true
    });
    setShowPermissionsEditor(true);
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    try {
      await roleAPI.update(selectedRole.id, roleForm);
      toast.success('Role updated successfully');
      setShowRoleModal(false);
      setSelectedRole(null);
      setRoleForm({ name: '', display_name: '', description: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to update role: ' + error.message);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.is_system_role) {
      toast.error('Cannot delete system roles');
      return;
    }

    if (role.user_count > 0) {
      toast.error('Cannot delete role that is assigned to users');
      return;
    }

    if (window.confirm(`Are you sure you want to delete the role "${role.display_name}"?`)) {
      try {
        await roleAPI.delete(role.id);
        toast.success('Role deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete role: ' + error.message);
      }
    }
  };

  const handleCreatePermission = async (e) => {
    e.preventDefault();
    try {
      await permissionAPI.create(permissionForm);
      toast.success('Permission created successfully');
      setShowPermissionModal(false);
      setPermissionForm({ name: '', display_name: '', description: '', category: '', resource: '', action: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to create permission: ' + error.message);
    }
  };

  const handleUpdatePermission = async (e) => {
    e.preventDefault();
    try {
      await permissionAPI.update(selectedPermission.id, permissionForm);
      toast.success('Permission updated successfully');
      setShowPermissionModal(false);
      setSelectedPermission(null);
      setPermissionForm({ name: '', display_name: '', description: '', category: '', resource: '', action: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to update permission: ' + error.message);
    }
  };

  const handleDeletePermission = async (permission) => {
    if (permission.is_system_permission) {
      toast.error('Cannot delete system permissions');
      return;
    }

    if (window.confirm(`Are you sure you want to delete the permission "${permission.display_name}"?`)) {
      try {
        await permissionAPI.delete(permission.id);
        toast.success('Permission deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete permission: ' + error.message);
      }
    }
  };

  const openRoleModal = (role = null) => {
    if (role) {
      setSelectedRole(role);
      setRoleForm({
        name: role.name,
        display_name: role.display_name,
        description: role.description || ''
      });
    } else {
      setSelectedRole(null);
      setRoleForm({ name: '', display_name: '', description: '' });
    }
    setShowRoleModal(true);
  };

  const openPermissionModal = (permission = null) => {
    if (permission) {
      setSelectedPermission(permission);
      setPermissionForm({
        name: permission.name,
        display_name: permission.display_name,
        description: permission.description || '',
        category: permission.category,
        resource: permission.resource,
        action: permission.action
      });
    } else {
      setSelectedPermission(null);
      setPermissionForm({ name: '', display_name: '', description: '', category: '', resource: '', action: '' });
    }
    setShowPermissionModal(true);
  };

  const openPermissionsEditor = async (role) => {
    try {
      const response = await roleAPI.getById(role.id);
      setSelectedRole(response.data);
      setShowPermissionsEditor(true);
    } catch (error) {
      toast.error('Failed to load role permissions: ' + error.message);
    }
  };

  const handleUpdateRolePermissions = async (rolePermissions) => {
    try {
      if (selectedRole.isNew) {
        // This is a new role creation flow - use the combined API
        const permissionIds = rolePermissions
          .filter(p => p.granted)
          .map(p => p.id);

        await roleAPI.createWithPermissions(newRoleData, permissionIds);

        toast.success('Role created successfully with permissions');
        setShowRoleCreationFlow(false);
        setNewRoleData(null);
      } else {
        // This is editing an existing role
        const permissionIds = rolePermissions
          .filter(p => p.granted)
          .map(p => p.id);

        await roleAPI.updatePermissions(selectedRole.id, permissionIds);
        toast.success('Role permissions updated successfully');
      }

      setShowPermissionsEditor(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update role permissions: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
          <div className="loading-spinner"></div>
          <span style={{ marginLeft: '12px' }}>Loading roles and permissions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content" style={{ position: 'relative', minHeight: '800px' }}>
      <style>
        {`
          @keyframes pulse-amber {
            0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
            100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
          }
        `}
      </style>

      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '24px',
        margin: '16px'
      }}>
        <div style={{
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '48px',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          maxWidth: '440px',
          transform: 'translateY(-40px)'
        }}>
          <div style={{
            width: '80px', height: '80px',
            background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
            borderRadius: '24px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 10px 25px -5px rgba(203, 213, 225, 0.5)',
            transform: 'rotate(-5deg)'
          }}>
            <span style={{ fontSize: '32px', transform: 'rotate(5deg)', display: 'block' }}>🛠️</span>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#037b90', marginBottom: '12px', letterSpacing: '-0.5px' }}>
            Under Maintenance
          </h2>
          <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.6', marginBottom: '32px' }}>
            We're currently upgrading the Roles &amp; Permissions architecture. This module will be back online shortly with enhanced capabilities.
          </p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '8px 16px', background: '#fffbeb', border: '1px solid #fef3c7',
            borderRadius: '20px', color: '#92400e', fontSize: '13px', fontWeight: '600'
          }}>
            <div style={{
              width: '8px', height: '8px', background: '#f59e0b',
              borderRadius: '50%', animation: 'pulse-amber 2s infinite'
            }}></div>
            Engineer at work
          </div>
        </div>
      </div>

      <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Roles & Permissions</h1>
            <p className="page-subtitle">
              Create user roles and assign permissions that control access to different parts of the application.
              <br />
              <strong>Permissions represent actual screens and features</strong> - assign them to roles to control what users can see and do.
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #e2e8f0',
            gap: '0'
          }}>
            <button
              className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'roles' ? '#037b90' : 'transparent',
                color: activeTab === 'roles' ? 'white' : '#64748b',
                borderRadius: '8px 8px 0 0',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              👥 Roles ({roles.length})
            </button>
            <button
              className={`tab-button ${activeTab === 'permissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('permissions')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'permissions' ? '#037b90' : 'transparent',
                color: activeTab === 'permissions' ? 'white' : '#64748b',
                borderRadius: '8px 8px 0 0',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              📋 Available Permissions ({permissionsList.length})
            </button>
          </div>
        </div>

        {/* Roles Tab */}
        {activeTab === 'roles' && (
          <div>
            <div className="card">
              <div className="card-header">
                <div>
                  <h2 className="card-title">System Roles</h2>
                  <p className="card-subtitle">
                    Create and manage user roles. When you create a new role, you'll select which application features it can access.
                    <br />
                    <strong>Tip:</strong> Think about what screens and actions each role should have access to.
                  </p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => openRoleModal()}
                >
                  ➕ Create New Role
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
                {roles.map(role => (
                  <div key={role.id} style={{
                    background: 'linear-gradient(135deg, rgba(3, 123, 144, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '20px',
                    position: 'relative'
                  }}>
                    {role.is_system_role && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: '#10b981',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        SYSTEM
                      </div>
                    )}

                    <div style={{ marginBottom: '16px' }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#037b90',
                        marginBottom: '4px'
                      }}>
                        {role.display_name}
                      </h3>
                      <p style={{
                        fontSize: '12px',
                        color: '#64748b',
                        fontFamily: 'monospace',
                        marginBottom: '8px'
                      }}>
                        {role.name}
                      </p>
                      <p style={{
                        fontSize: '14px',
                        color: '#374151',
                        lineHeight: '1.4'
                      }}>
                        {role.description}
                      </p>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '8px'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#037b90' }}>
                          {role.user_count}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Users</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#ff7f50' }}>
                          {role.permission_count}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Permissions</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => openPermissionsEditor(role)}
                        style={{ flex: 1 }}
                      >
                        {role.is_system_role ? '👁️ View Permissions' : '🔐 Permissions'}
                      </button>
                      {!role.is_system_role && (
                        <>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => openRoleModal(role)}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleDeleteRole(role)}
                            style={{ color: '#dc2626' }}
                            disabled={role.user_count > 0}
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Permissions Tab */}
        {activeTab === 'permissions' && (
          <div>
            <div className="card">
              <div className="card-header">
                <div>
                  <h2 className="card-title">Available System Permissions</h2>
                  <p className="card-subtitle">
                    These permissions correspond to actual screens and features in the application.
                    <br />
                    <strong>Use these permissions when creating roles</strong> - they control what users can access.
                  </p>
                </div>
                {/* Only show add button for developers/super admins - commented out for regular use */}
                {/* 
              <button 
                className="btn btn-secondary"
                onClick={() => openPermissionModal()}
                title="Only for developers adding new features"
                style={{ display: 'none' }}
              >
                ➕ Add System Permission
              </button>
              */}
              </div>

              {Object.entries(permissions).map(([category, categoryPermissions]) => (
                <div key={category} style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#037b90',
                    marginBottom: '8px',
                    padding: '8px 0',
                    borderBottom: '2px solid #037b90'
                  }}>
                    {category} ({categoryPermissions.length})
                  </h3>
                  <p style={{
                    fontSize: '12px',
                    color: '#64748b',
                    marginBottom: '16px',
                    fontStyle: 'italic'
                  }}>
                    {getCategoryDescription(category)}
                  </p>

                  <div style={{
                    background: '#f8fafc',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <p style={{
                      fontSize: '12px',
                      color: '#475569',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      💡 <strong>These permissions control access to actual screens and features in the application.</strong>
                      When you assign these to roles, users with those roles will be able to access the corresponding functionality.
                    </p>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                    {categoryPermissions.map(permission => (
                      <div key={permission.id} style={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '16px',
                        position: 'relative'
                      }}>
                        {permission.is_system_permission && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: '#3b82f6',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '9px',
                            fontWeight: '600'
                          }}>
                            SYSTEM
                          </div>
                        )}

                        <div style={{ marginBottom: '12px' }}>
                          <h4 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '4px'
                          }}>
                            {permission.display_name}
                          </h4>
                          <p style={{
                            fontSize: '11px',
                            color: '#64748b',
                            fontFamily: 'monospace',
                            marginBottom: '6px'
                          }}>
                            {permission.name}
                          </p>
                          <p style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            lineHeight: '1.3'
                          }}>
                            {permission.description}
                          </p>
                        </div>

                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          marginBottom: '12px',
                          fontSize: '10px'
                        }}>
                          <span style={{
                            background: '#f3f4f6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            color: '#374151'
                          }}>
                            {permission.resource}
                          </span>
                          <span style={{
                            background: '#fef3c7',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            color: '#92400e'
                          }}>
                            {permission.action}
                          </span>
                        </div>

                        {!permission.is_system_permission && false && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => openPermissionModal(permission)}
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => handleDeletePermission(permission)}
                              style={{ fontSize: '11px', padding: '4px 8px', color: '#dc2626' }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Role Modal */}
        {showRoleModal && (
          <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{selectedRole ? 'Edit Role Details' : 'Create New Role'}</h3>
                <button className="modal-close" onClick={() => setShowRoleModal(false)}>×</button>
              </div>

              <form onSubmit={selectedRole ? handleUpdateRole : handleCreateRole}>
                <div className="form-group">
                  <label className="required">Role Name (Internal)</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    placeholder="e.g., custom_manager"
                    pattern="[a-z_]+"
                    title="Only lowercase letters and underscores allowed"
                    required
                    disabled={selectedRole?.is_system_role}
                  />
                  <div className="form-help">
                    Used internally by the system. Use lowercase letters and underscores only.
                  </div>
                </div>

                <div className="form-group">
                  <label className="required">Display Name</label>
                  <input
                    type="text"
                    value={roleForm.display_name}
                    onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                    placeholder="e.g., Custom Manager"
                    required
                  />
                  <div className="form-help">
                    Friendly name shown to users in the interface.
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                    placeholder="Describe the role's purpose and responsibilities"
                    rows="3"
                  />
                  <div className="form-help">
                    Optional description to help users understand this role's purpose.
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowRoleModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {selectedRole ? 'Update Role' : 'Next: Set Permissions →'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Permission Modal - Hidden for regular use */}
        {false && showPermissionModal && (
          <div className="modal-overlay" onClick={() => setShowPermissionModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{selectedPermission ? 'Edit System Permission' : 'Add New System Permission'}</h3>
                <button className="modal-close" onClick={() => setShowPermissionModal(false)}>×</button>
              </div>

              <div style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '12px',
                margin: '0 20px 20px 20px'
              }}>
                <p style={{ color: '#92400e', margin: 0, fontSize: '13px' }}>
                  ⚠️ <strong>System Administrator Only:</strong> Only create new permissions when adding new features to the application.
                  Most users should only assign existing permissions to roles.
                </p>
              </div>

              <form onSubmit={selectedPermission ? handleUpdatePermission : handleCreatePermission}>
                <div className="form-group">
                  <label className="required">Permission Name (Internal)</label>
                  <input
                    type="text"
                    value={permissionForm.name}
                    onChange={(e) => setPermissionForm({ ...permissionForm, name: e.target.value })}
                    placeholder="e.g., custom.action"
                    pattern="[a-z._]+"
                    title="Only lowercase letters, dots, and underscores allowed"
                    required
                    disabled={selectedPermission?.is_system_permission}
                  />
                  <div className="form-help">
                    Internal identifier using format: resource.action (e.g., users.create)
                  </div>
                </div>

                <div className="form-group">
                  <label className="required">Display Name</label>
                  <input
                    type="text"
                    value={permissionForm.display_name}
                    onChange={(e) => setPermissionForm({ ...permissionForm, display_name: e.target.value })}
                    placeholder="e.g., Custom Action"
                    required
                  />
                  <div className="form-help">
                    User-friendly name shown in the permissions list.
                  </div>
                </div>

                <div className="form-group">
                  <label className="required">Category</label>
                  <select
                    value={permissionForm.category}
                    onChange={(e) => setPermissionForm({ ...permissionForm, category: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                    <option value="Custom">Custom</option>
                  </select>
                  <div className="form-help">
                    Groups related permissions together for better organization.
                  </div>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="required">Resource</label>
                    <input
                      type="text"
                      value={permissionForm.resource}
                      onChange={(e) => setPermissionForm({ ...permissionForm, resource: e.target.value })}
                      placeholder="e.g., users, reports"
                      required
                    />
                    <div className="form-help">
                      What this permission controls (e.g., users, payments)
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="required">Action</label>
                    <input
                      type="text"
                      value={permissionForm.action}
                      onChange={(e) => setPermissionForm({ ...permissionForm, action: e.target.value })}
                      placeholder="e.g., view, create, edit"
                      required
                    />
                    <div className="form-help">
                      What action is allowed (e.g., view, create, delete)
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={permissionForm.description}
                    onChange={(e) => setPermissionForm({ ...permissionForm, description: e.target.value })}
                    placeholder="Describe what this permission allows"
                    rows="3"
                  />
                  <div className="form-help">
                    Detailed explanation of what this permission grants access to.
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPermissionModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {selectedPermission ? 'Update Permission' : 'Add System Permission'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Permissions Editor Modal */}
        {showPermissionsEditor && selectedRole && (
          <PermissionsEditor
            role={selectedRole}
            isNewRole={selectedRole.isNew || false}
            onSave={handleUpdateRolePermissions}
            onClose={() => {
              setShowPermissionsEditor(false);
              if (selectedRole.isNew) {
                // If canceling new role creation, reset the form
                setShowRoleCreationFlow(false);
                setNewRoleData(null);
                setRoleForm({ name: '', display_name: '', description: '' });
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

// Permissions Editor Component
const PermissionsEditor = ({ role, isNewRole = false, onSave, onClose }) => {
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setRolePermissions(role.permissions || []);
    setLoading(false);
  }, [role]);

  const togglePermission = (permissionId) => {
    if (role.is_system_role && !isNewRole) return;
    setRolePermissions(prev =>
      prev.map(p =>
        p.id === permissionId
          ? { ...p, granted: !p.granted }
          : p
      )
    );
  };

  const toggleCategory = (category) => {
    if (role.is_system_role && !isNewRole) return;
    const categoryPermissions = rolePermissions.filter(p => p.category === category);
    const allGranted = categoryPermissions.every(p => p.granted);

    setRolePermissions(prev =>
      prev.map(p =>
        p.category === category
          ? { ...p, granted: !allGranted }
          : p
      )
    );
  };

  const groupedPermissions = rolePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div className="loading-spinner"></div>
            <p>Loading permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" style={{ maxWidth: '800px', maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {isNewRole ? `Set Permissions for New Role: ${role.display_name}` :
              role.is_system_role ? `View Permissions: ${role.display_name} (Read Only)` :
                `Manage Permissions: ${role.display_name}`}
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '20px' }}>
          {isNewRole && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: '#0369a1', margin: '0 0 8px 0', fontSize: '14px' }}>
                🎯 Setting up permissions for your new role
              </h4>
              <p style={{ color: '#0369a1', margin: 0, fontSize: '12px' }}>
                Select the permissions this role should have. You can always modify these later.
              </p>
            </div>
          )}

          {role.is_system_role && !isNewRole && (
            <div style={{
              background: '#fff7ed',
              border: '1px solid #fdba74',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: '#9a3412', margin: '0 0 4px 0', fontSize: '14px' }}>
                🔒 System Protected Role
              </h4>
              <p style={{ color: '#9a3412', margin: 0, fontSize: '12px', lineHeight: '1.4' }}>
                This is a core system role. Its permissions are carefully balanced for system security and stability
                and cannot be modified through the interface.
              </p>
            </div>
          )}

          {Object.entries(groupedPermissions).map(([category, permissions]) => {
            const grantedCount = permissions.filter(p => p.granted).length;
            const totalCount = permissions.length;

            return (
              <div key={category} style={{ marginBottom: '24px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleCategory(category)}
                >
                  <input
                    type="checkbox"
                    checked={grantedCount === totalCount}
                    onChange={() => toggleCategory(category)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#037b90',
                    margin: 0,
                    flex: 1
                  }}>
                    {category}
                  </h4>
                  <span style={{
                    fontSize: '12px',
                    color: '#64748b',
                    background: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px'
                  }}>
                    {grantedCount}/{totalCount}
                  </span>
                </div>

                <div style={{ paddingLeft: '24px' }}>
                  {permissions.map(permission => (
                    <div
                      key={permission.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        marginBottom: '4px',
                        background: permission.granted ? 'rgba(16, 185, 129, 0.1)' : 'white',
                        border: '1px solid #e2e8f0',
                        cursor: 'pointer'
                      }}
                      onClick={() => togglePermission(permission.id)}
                    >
                      <input
                        type="checkbox"
                        checked={permission.granted || false}
                        onChange={() => togglePermission(permission.id)}
                        disabled={role.is_system_role && !isNewRole}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#374151'
                        }}>
                          {permission.display_name}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#64748b'
                        }}>
                          {permission.description}
                        </div>
                      </div>
                      <div style={{
                        fontSize: '10px',
                        color: '#64748b',
                        fontFamily: 'monospace'
                      }}>
                        {permission.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {isNewRole ? 'Cancel Role Creation' : 'Cancel'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSave(rolePermissions)}
            disabled={role.is_system_role && !isNewRole}
          >
            {isNewRole ? 'Create Role with Permissions' :
              role.is_system_role ? 'System Protected' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;